import { Checkbox, CheckboxProps, FormControl, FormControlLabel, FormHelperText } from '@material-ui/core';
import { ControllerRenderProps, FieldError, FieldValues, Path } from 'react-hook-form';

import { ConditionalWrapper } from '@/components/shared/components/ConditionalWrapper';
import { Font } from '@/components/shared/components/Font';
import { useCustomFieldStyles } from '@/components/shared/components/NewContextForms/hooks/useCustomFieldStyles';
import {
  CustomFormLabel,
  CustomFormLabelProps,
} from '@/components/shared/components/NewContextForms/shared/CustomFormLabel';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { StyledTooltip } from '@/components/shared/components/StyledTooltip';

export interface CustomCheckboxFieldProps<TFieldValues extends FieldValues>
  extends Omit<CheckboxProps, 'name' | 'error' | 'style'> {
  fieldVariant?: FieldVariant;
  field?: ControllerRenderProps<FieldValues, Path<TFieldValues>>;
  error?: FieldError | undefined;
  label?: string | number | JSX.Element;
  labelProps?: Omit<CustomFormLabelProps, 'label'>;
  tooltipTitle?: string | number | JSX.Element;
}

// NOTE: By default, one line is forced.
// Disable by adding the prop
// labelProps={{ forceSingleLine: false }}

export function CustomCheckboxField<TFieldValues extends FieldValues>({
  fieldVariant = FieldVariant.dataRow,
  field,
  error,
  label,
  labelProps,
  tooltipTitle,
  ...props
}: CustomCheckboxFieldProps<TFieldValues>) {
  const { getMUIClasses } = useCustomFieldStyles({
    fieldVariant,
    field: !!field,
    disabled: props.disabled,
  });

  const classes = getMUIClasses();
  const fieldVariantIsForm = fieldVariant === FieldVariant.form;
  return (
    <FormControl fullWidth classes={classes} style={fieldVariantIsForm ? { marginBottom: '2px' } : {}}>
      <ConditionalWrapper
        condition={!!tooltipTitle}
        wrapper={(children) => (
          <StyledTooltip title={tooltipTitle as string} tooltipProps={{ placement: 'right' }}>
            {children}
          </StyledTooltip>
        )}
      >
        <FormControlLabel
          style={{ marginLeft: 0, alignItems: 'flex-start' }}
          control={
            <Checkbox
              {...props}
              checked={field?.value || false}
              color={'secondary'}
              style={{ padding: 0, marginRight: 6, marginBottom: 1 }}
              {...field}
              {...(error && {
                error: !!error,
              })}
            />
          }
          label={
            typeof label === 'string' ? (
              <CustomFormLabel label={label} {...labelProps} variant={'body2'} color={'textPrimary'} />
            ) : (
              <Font variant={'body2'} color={'textPrimary'}>
                {label}
              </Font>
            )
          }
        />
      </ConditionalWrapper>
      <FormHelperText error={!!error}>{error?.message}</FormHelperText>
    </FormControl>
  );
}
